import { useMemo, useState } from "react";
import { TOPIC_LABELS, TopicKey } from "../api/lessonReports";
import {
  BranchOverviewRow,
  DailySummaryRow,
  InstructorSummaryRow,
  LessonScheduleScope,
  ReportExportType,
  StudentSummaryRow,
  TopicsCoverageRow,
  VehicleSummaryRow,
  downloadReport,
  useBranchOverview,
  useDailySummary,
  useInstructorSummary,
  useStudentSummary,
  useTopicsCoverage,
  useVehicleSummary,
} from "../api/reports";
import { nextServiceDateStatus, nextServiceStatus } from "../api/vehicles";
import { ScheduleExportButton } from "../components/schedule/ScheduleExportButton";
import {
  CohortFilterBar,
  type CohortFilterValue,
} from "../components/cohorts/CohortFilterBar";
import {
  DEFAULT_STUDENT_LIST_STATUS_FILTER,
  StudentListStatusFilterSelect,
  type StudentListStatusFilter,
} from "../components/common/StudentListStatusFilterSelect";
import { useAuth } from "../auth/AuthContext";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./ReportsPage.css";

type TabId = "instructor" | "vehicle" | "student" | "daily" | "topics" | "branch" | "schedule";

const TAB_TO_EXPORT_TYPE: Record<Exclude<TabId, "schedule">, ReportExportType> = {
  instructor: "instructor-summary",
  vehicle: "vehicle-summary",
  student: "student-summary",
  daily: "daily-summary",
  topics: "topics-coverage",
  branch: "branch-overview",
};

function fmtMinutes(min: number): string {
  if (!min) return "0 dk";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} dk`;
  if (m === 0) return `${h} sa`;
  return `${h} sa ${m} dk`;
}

function fmtDateOnly(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

function fmtDay(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", weekday: "short" });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function Bar({ value, max, color = "#3b82f6" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="rep-bar">
      <div className="rep-bar__fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function ReportsPage() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const isAdmin = role === "tenant_admin";

  const [tab, setTab] = useState<TabId>("instructor");
  const [startDate, setStartDate] = useState<string>(daysAgoIso(30));
  const [endDate, setEndDate] = useState<string>(todayIso());
  const [downloading, setDownloading] = useState<"csv" | "xlsx" | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (format: "csv" | "xlsx") => {
    if (tab === "schedule") return;
    setDownloading(format);
    setDownloadError(null);
    try {
      await downloadReport(TAB_TO_EXPORT_TYPE[tab], format, {
        start_date: startDate,
        end_date: endDate,
      });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "İndirme Başarısız oldu.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="rep-page">
      <header className="rep-page__header">
        <div>
          <h2>Raporlar</h2>
          <p className="rep-page__sub">
            Tum raporlar `appointments` + `lesson_reports` + `vehicles` tablolari uzerinde anlik
            agregasyonla uretilir. Eklenen her ders raporu raporlari otomatik Günceller.
          </p>
        </div>
        <div className="rep-page__date-row">
          <label>
            Baslangic
            <input
              type="date"
              className="kp-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            Bitis
            <input
              type="date"
              className="kp-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <div className="rep-page__quick">
            <button type="button" onClick={() => { setStartDate(daysAgoIso(7)); setEndDate(todayIso()); }} className="kp-btn kp-btn--small">
              Son 7 Gün
            </button>
            <button type="button" onClick={() => { setStartDate(daysAgoIso(30)); setEndDate(todayIso()); }} className="kp-btn kp-btn--small">
              Son 30 Gün
            </button>
            <button type="button" onClick={() => { setStartDate(daysAgoIso(90)); setEndDate(todayIso()); }} className="kp-btn kp-btn--small">
              Son 90 Gün
            </button>
          </div>
          {tab !== "schedule" && (
            <div className="rep-page__export">
              <button
                type="button"
                className="kp-btn kp-btn--small"
                onClick={() => handleDownload("xlsx")}
                disabled={downloading !== null}
              >
                {downloading === "xlsx" ? "Hazirlaniyor..." : "Excel Indir"}
              </button>
              <button
                type="button"
                className="kp-btn kp-btn--small"
                onClick={() => handleDownload("csv")}
                disabled={downloading !== null}
              >
                {downloading === "csv" ? "Hazirlaniyor..." : "CSV Indir"}
              </button>
            </div>
          )}
        </div>
        {downloadError && <div className="rep-page__error" style={{ marginTop: 8 }}>{downloadError}</div>}
      </header>

      <nav className="rep-tabs">
        <button type="button" className={tab === "instructor" ? "is-active" : ""} onClick={() => setTab("instructor")}>
          Eğitmen Ozeti
        </button>
        <button type="button" className={tab === "vehicle" ? "is-active" : ""} onClick={() => setTab("vehicle")}>
          Araç Ozeti
        </button>
        <button type="button" className={tab === "student" ? "is-active" : ""} onClick={() => setTab("student")}>
          Kursiyer Ozeti
        </button>
        <button type="button" className={tab === "schedule" ? "is-active" : ""} onClick={() => setTab("schedule")}>
          Ders Programi
        </button>
        <button type="button" className={tab === "daily" ? "is-active" : ""} onClick={() => setTab("daily")}>
          Gunluk Akis
        </button>
        <button type="button" className={tab === "topics" ? "is-active" : ""} onClick={() => setTab("topics")}>
          Konu Kapsamasi
        </button>
        {isAdmin && (
          <button type="button" className={tab === "branch" ? "is-active" : ""} onClick={() => setTab("branch")}>
            Şube Genel
          </button>
        )}
      </nav>

      <section className="rep-tab-content">
        {tab === "instructor" && <InstructorTab startDate={startDate} endDate={endDate} />}
        {tab === "vehicle" && <VehicleTab startDate={startDate} endDate={endDate} />}
        {tab === "student" && <StudentTab startDate={startDate} endDate={endDate} />}
        {tab === "daily" && <DailyTab startDate={startDate} endDate={endDate} />}
        {tab === "topics" && <TopicsTab startDate={startDate} endDate={endDate} />}
        {tab === "branch" && isAdmin && <BranchTab startDate={startDate} endDate={endDate} />}
        {tab === "schedule" && <ScheduleExportTab />}
      </section>
    </div>
  );
}

function startOfWeekMondayFromIso(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

function endOfWeekFromStart(weekStartIso: string): string {
  const [y, m, d] = weekStartIso.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  date.setDate(date.getDate() + 6);
  return date.toISOString().slice(0, 10);
}

function endOfMonthFromIso(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m || 1, 0).toISOString().slice(0, 10);
}

function ScheduleExportTab() {
  const [scope, setScope] = useState<LessonScheduleScope>("weekly");
  const [anchorDate, setAnchorDate] = useState(todayIso());

  const range = useMemo(() => {
    if (scope === "daily") {
      return { startDate: anchorDate, endDate: anchorDate };
    }
    if (scope === "weekly") {
      const startDate = startOfWeekMondayFromIso(anchorDate);
      return { startDate, endDate: endOfWeekFromStart(startDate) };
    }
    const [y, m] = anchorDate.split("-").map(Number);
    const startDate = `${y}-${String(m).padStart(2, "0")}-01`;
    return { startDate, endDate: endOfMonthFromIso(startDate) };
  }, [scope, anchorDate]);

  return (
    <div className="rep-schedule-export kp-card">
      <h3>Ders programi Excel ciktisi</h3>
      <p className="rep-page__sub">
        Secilen Dönemdeki tum randevulari Satır Satır listeler (tarih, saat, egitmen, kursiyer, arac,
        ders tipi, durum). Takvimde gordugunuz programin Excel karsiligidir.
      </p>
      <div className="rep-schedule-export__controls">
        <label>
          Gorunum
          <select
            className="kp-input"
            value={scope}
            onChange={(e) => setScope(e.target.value as LessonScheduleScope)}
          >
            <option value="daily">Gunluk</option>
            <option value="weekly">Haftalik</option>
            <option value="monthly">Aylik</option>
          </select>
        </label>
        <label>
          {scope === "monthly" ? "Ay (herhangi bir Gün)" : scope === "weekly" ? "Hafta (herhangi bir Gün)" : "Gun"}
          <input
            type="date"
            className="kp-input"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
          />
        </label>
        <p className="rep-schedule-export__range">
          Aralik: <strong>{fmtDay(range.startDate)}</strong>
          {range.startDate !== range.endDate && (
            <>
              {" "}
              – <strong>{fmtDay(range.endDate)}</strong>
            </>
          )}
        </p>
        <ScheduleExportButton scope={scope} startDate={range.startDate} endDate={range.endDate} />
      </div>
    </div>
  );
}

function InstructorTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, loading, error } = useInstructorSummary({ startDate, endDate });
  const items = data?.items ?? [];
  const maxMinutes = useMemo(() => Math.max(1, ...items.map((i) => i.total_lesson_minutes)), [items]);

  useRegisterListPageStats({
    label: "eğitmen",
    visible: items.length,
    loading,
  });

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;
  if (!items.length) return <div className="rep-page__hint">Bu Dönem icin egitmen verisi yok.</div>;

  const totals = items.reduce(
    (acc, r) => ({
      lessons: acc.lessons + r.completed,
      minutes: acc.minutes + r.total_lesson_minutes,
      km: acc.km + r.total_km,
      students: acc.students + r.unique_students,
    }),
    { lessons: 0, minutes: 0, km: 0, students: 0 },
  );

  return (
    <>
      <div className="rep-kpi-row">
        <Kpi label="Toplam tamamlanan ders" value={totals.lessons.toString()} />
        <Kpi label="Toplam ders suresi" value={fmtMinutes(totals.minutes)} />
        <Kpi label="Toplam KM" value={`${totals.km.toLocaleString("tr-TR")} km`} />
        <Kpi label="Eğitmen sayisi" value={items.length.toString()} />
      </div>
      <div className="rep-table-wrap">
        <table className="rep-table kp-data-table">
          <thead>
            <tr>
              <th>Eğitmen</th>
              <th>Tip</th>
              <th>Durum</th>
              <th>Tamamlanan</th>
              <th>İptal</th>
              <th>Gelmedi</th>
              <th>Toplam Sure</th>
              <th>Kursiyer</th>
              <th>KM</th>
              <th>Skor</th>
              <th className="rep-table__bar">Yogunluk</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row: InstructorSummaryRow) => (
              <tr key={row.instructor_id}>
                <td>
                  <strong>{row.instructor_name}</strong>
                </td>
                <td>{row.employment_type === "full_time" ? "Tam zamanli" : "Yevmiyeli"}</td>
                <td>{row.is_active ? <span className="badge badge-ok">Aktif</span> : <span className="badge badge-warn">Pasif</span>}</td>
                <td className="mono">{row.completed}</td>
                <td className="mono">{row.cancelled}</td>
                <td className="mono">{row.no_show_count}</td>
                <td className="mono">{fmtMinutes(row.total_lesson_minutes)}</td>
                <td className="mono">{row.unique_students}</td>
                <td className="mono">{row.total_km} km</td>
                <td className="mono">{row.avg_driving_score ? `${row.avg_driving_score}/5` : "-"}</td>
                <td className="rep-table__bar">
                  <Bar value={row.total_lesson_minutes} max={maxMinutes} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function VehicleTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, loading, error } = useVehicleSummary({ startDate, endDate });
  const items = data?.items ?? [];

  useRegisterListPageStats({
    label: "araç",
    visible: items.length,
    loading,
  });

  const maxKm = useMemo(() => Math.max(1, ...items.map((i) => i.total_km)), [items]);

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;
  if (!items.length) return <div className="rep-page__hint">Bu Dönem icin arac verisi yok.</div>;

  const totals = items.reduce(
    (acc, r) => ({
      lessons: acc.lessons + r.completed,
      km: acc.km + r.total_km,
      minutes: acc.minutes + r.total_lesson_minutes,
    }),
    { lessons: 0, km: 0, minutes: 0 },
  );

  return (
    <>
      <div className="rep-kpi-row">
        <Kpi label="Toplam ders" value={totals.lessons.toString()} />
        <Kpi label="Toplam KM" value={`${totals.km.toLocaleString("tr-TR")} km`} />
        <Kpi label="Toplam ders suresi" value={fmtMinutes(totals.minutes)} />
        <Kpi label="Araç sayisi" value={items.length.toString()} />
      </div>
      <div className="rep-table-wrap">
        <table className="rep-table kp-data-table">
          <thead>
            <tr>
              <th>Plaka</th>
              <th>Marka/Model</th>
              <th>Vites</th>
              <th>Durum</th>
              <th>Tamamlanan</th>
              <th>Toplam Sure</th>
              <th>KM (Dönem)</th>
              <th>Guncel KM</th>
              <th>Sonraki Bakım</th>
              <th className="rep-table__bar">KM Yogunluk</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row: VehicleSummaryRow) => {
              const kmStatus = nextServiceStatus(row.current_km, row.next_service_km);
              const dateStatus = nextServiceDateStatus(row.next_service_date);
              const tone =
                kmStatus.tone === "expired" || dateStatus.tone === "expired"
                  ? "warn"
                  : kmStatus.tone === "very-soon" || dateStatus.tone === "very-soon"
                  ? "warn"
                  : kmStatus.tone === "soon" || dateStatus.tone === "soon"
                  ? "info"
                  : kmStatus.tone === "ok" || dateStatus.tone === "ok"
                  ? "ok"
                  : "muted";
              return (
                <tr key={row.vehicle_id}>
                  <td className="mono">
                    <strong>{row.plate_no}</strong>
                  </td>
                  <td>{row.brand_model || "-"}</td>
                  <td>{row.gear_type === "manual" ? "Manuel" : row.gear_type === "automatic" ? "Otomatik" : row.gear_type || "-"}</td>
                  <td>{row.is_active ? <span className="badge badge-ok">Aktif</span> : <span className="badge badge-warn">Pasif</span>}</td>
                  <td className="mono">{row.completed}</td>
                  <td className="mono">{fmtMinutes(row.total_lesson_minutes)}</td>
                  <td className="mono">{row.total_km.toLocaleString("tr-TR")} km</td>
                  <td className="mono">{row.current_km.toLocaleString("tr-TR")} km</td>
                  <td className={`rep-maintenance rep-maintenance--${tone}`}>
                    <div className="rep-maintenance__row">
                      <span>
                        {row.next_service_km !== null
                          ? `${row.next_service_km.toLocaleString("tr-TR")} km`
                          : "-"}
                      </span>
                      <small>{kmStatus.label}</small>
                    </div>
                    <div className="rep-maintenance__row">
                      <span>{fmtDateOnly(row.next_service_date)}</span>
                      <small>{dateStatus.label}</small>
                    </div>
                  </td>
                  <td className="rep-table__bar">
                    <Bar value={row.total_km} max={maxKm} color="#10b981" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StudentTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [statusFilter, setStatusFilter] = useState<StudentListStatusFilter>(
    DEFAULT_STUDENT_LIST_STATUS_FILTER,
  );
  const [cohortFilter, setCohortFilter] = useState<CohortFilterValue>({
    periodId: "",
    groupId: "",
    subgroupId: "",
  });
  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(
    JSON.stringify([startDate, endDate, statusFilter, cohortFilter, pageSize]),
  );
  const { data, loading, error } = useStudentSummary({
    startDate,
    endDate,
    status: statusFilter,
    trainingPeriodId: cohortFilter.periodId || undefined,
    trainingGroupId: cohortFilter.groupId || undefined,
    trainingSubgroupId: cohortFilter.subgroupId || undefined,
    limit: pageSize,
    offset,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  useRegisterListPageStats({
    label: "kursiyer",
    visible: items.length,
    total,
    loading,
  });

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;

  return (
    <>
      <div className="rep-toolbar rep-toolbar--stack">
        <CohortFilterBar
          value={cohortFilter}
          onChange={(v) => {
            setCohortFilter(v);
          }}
        />
        <StudentListStatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
      </div>
      <div className="rep-table-wrap">
        <table className="rep-table kp-data-table">
          <thead>
            <tr>
              <th>Kursiyer</th>
              <th>Kayit / Grup / Alt</th>
              <th>Durum</th>
              <th>Direksiyon Sınavı</th>
              <th>Kalan</th>
              <th>Yazılan</th>
              <th>Katıldı</th>
              <th>İptal</th>
              <th>Gelmedi</th>
              <th>Toplam Sure</th>
              <th>KM</th>
              <th>Skor</th>
              <th>Eğitmen Sayisi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row: StudentSummaryRow) => (
              <tr key={row.student_id}>
                <td>
                  <strong>{row.student_name}</strong>
                </td>
                <td className="rep-table__cohort">
                  {row.training_period_label && (
                    <span>{row.training_period_label}</span>
                  )}
                  {row.training_group_label && <span>{row.training_group_label}</span>}
                  {row.training_subgroup_label && (
                    <span>{row.training_subgroup_label}</span>
                  )}
                  {!row.training_period_label && !row.training_group_label && "—"}
                </td>
                <td>
                  {row.student_status === "active" ? (
                    <span className="badge badge-ok">Aktif</span>
                  ) : row.student_status === "graduated" ? (
                    <span className="badge badge-info">Mezun</span>
                  ) : (
                    <span className="badge badge-warn">Pasif</span>
                  )}
                </td>
                <td>{fmtDateOnly(row.exam_date)}</td>
                <td>
                  {row.days_to_exam === null ? "-" : row.days_to_exam < 0 ? (
                    <span className="badge badge-warn">{Math.abs(row.days_to_exam)} Gün gecti</span>
                  ) : (
                    <span className={row.days_to_exam <= 14 ? "badge badge-warn" : "mono"}>{row.days_to_exam} Gün</span>
                  )}
                </td>
                <td className="mono">
                  {row.total_written ?? row.total_appointments - row.cancelled}
                </td>
                <td className="mono">{row.reported_attended}</td>
                <td className="mono">{row.cancelled}</td>
                <td className="mono">{row.no_show_count}</td>
                <td className="mono">{fmtMinutes(row.total_lesson_minutes)}</td>
                <td className="mono">{row.total_km} km</td>
                <td className="mono">{row.avg_driving_score ? `${row.avg_driving_score}/5` : "-"}</td>
                <td className="mono">{row.unique_instructors}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ListPaginationBar
        total={total}
        offset={offset}
        limit={pageSize}
        loading={loading}
        onOffsetChange={setOffset}
      />
    </>
  );
}

function DailyTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, loading, error } = useDailySummary({ startDate, endDate });
  const items = data?.items ?? [];

  useRegisterListPageStats({
    label: "gün",
    visible: items.length,
    loading,
  });

  const maxCompleted = useMemo(() => Math.max(1, ...items.map((d) => d.completed)), [items]);

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;
  if (!items.length) return <div className="rep-page__hint">Bu Dönem icin Günluk veri yok.</div>;

  const totals = items.reduce(
    (acc, d) => ({
      completed: acc.completed + d.completed,
      cancelled: acc.cancelled + d.cancelled,
      noShow: acc.noShow + d.no_show_count,
      minutes: acc.minutes + d.total_lesson_minutes,
      km: acc.km + d.total_km,
    }),
    { completed: 0, cancelled: 0, noShow: 0, minutes: 0, km: 0 },
  );

  return (
    <>
      <div className="rep-kpi-row">
        <Kpi label="Tamamlanan" value={totals.completed.toString()} />
        <Kpi label="İptal" value={totals.cancelled.toString()} />
        <Kpi label="Gelmedi" value={totals.noShow.toString()} />
        <Kpi label="Toplam Sure" value={fmtMinutes(totals.minutes)} />
        <Kpi label="Toplam KM" value={`${totals.km.toLocaleString("tr-TR")} km`} />
      </div>
      <div className="rep-daily">
        {items.map((d: DailySummaryRow) => (
          <div key={d.day} className="rep-daily__row">
            <div className="rep-daily__day">{fmtDay(d.day)}</div>
            <div className="rep-daily__bar">
              <Bar value={d.completed} max={maxCompleted} color="#10b981" />
              <div className="rep-daily__label">
                <strong>{d.completed}</strong> tamamlanan · {fmtMinutes(d.total_lesson_minutes)} · {d.total_km} km
                {d.cancelled > 0 && <span className="rep-daily__warn"> · {d.cancelled} iptal</span>}
                {d.no_show_count > 0 && <span className="rep-daily__warn"> · {d.no_show_count} gelmedi</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TopicsTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, loading, error } = useTopicsCoverage({ startDate, endDate });
  const items = data?.items ?? [];
  const total = data?.total_attended_reports ?? 0;

  useRegisterListPageStats({
    label: "konu",
    visible: items.length,
    loading,
  });

  const maxCount = useMemo(() => Math.max(1, ...items.map((t) => t.report_count)), [items]);

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;
  if (total === 0) return <div className="rep-page__hint">Bu Dönemde ders raporu yok.</div>;

  return (
    <>
      <div className="rep-kpi-row">
        <Kpi label="Donemdeki tamamlanan rapor" value={total.toString()} />
        <Kpi label="Farkli konu adedi" value={items.length.toString()} />
      </div>
      <div className="rep-topics">
        {items.map((row: TopicsCoverageRow) => (
          <div key={row.topic_key} className="rep-topics__row">
            <div className="rep-topics__label">{TOPIC_LABELS[row.topic_key as TopicKey] || row.topic_key}</div>
            <div className="rep-topics__bar-wrap">
              <Bar value={row.report_count} max={maxCount} color="#6366f1" />
            </div>
            <div className="rep-topics__num">
              <strong>{row.report_count}</strong> ders · %{row.percentage.toFixed(1)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function BranchTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, loading, error } = useBranchOverview({ startDate, endDate });
  const items = data?.items ?? [];

  useRegisterListPageStats({
    label: "şube",
    visible: items.length,
    loading,
  });

  if (loading) return <div className="rep-page__hint">{UI_LOADING}</div>;
  if (error) return <div className="rep-page__error">{error}</div>;
  if (!items.length) return <div className="rep-page__hint">Şube verisi yok.</div>;

  return (
    <div className="rep-table-wrap">
      <table className="rep-table kp-data-table">
        <thead>
          <tr>
            <th>Sube</th>
            <th>Aktif Kursiyer</th>
            <th>Mezun</th>
            <th>Aktif Eğitmen</th>
            <th>Aktif Arac</th>
            <th>Tamamlanan Ders</th>
            <th>İptal</th>
            <th>Toplam Sure</th>
            <th>Toplam KM</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row: BranchOverviewRow) => (
            <tr key={row.branch_id}>
              <td>
                <strong>{row.branch_name}</strong>
              </td>
              <td className="mono">{row.active_students}</td>
              <td className="mono">{row.graduated_students}</td>
              <td className="mono">{row.active_instructors}</td>
              <td className="mono">{row.active_vehicles}</td>
              <td className="mono">{row.completed}</td>
              <td className="mono">{row.cancelled}</td>
              <td className="mono">{fmtMinutes(row.total_lesson_minutes)}</td>
              <td className="mono">{row.total_km.toLocaleString("tr-TR")} km</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rep-kpi">
      <div className="rep-kpi__label">{label}</div>
      <div className="rep-kpi__value">{value}</div>
    </div>
  );
}

